import React, { useState, useEffect } from 'react';
import './App.css';
import RoomJoin from './components/RoomJoin';
import GameRoom from './components/GameRoom';
import rabbiLogo from './assets/rabbi.png';

const translations = {
  he: {
    direction: 'rtl',
    toggleLabel: 'English',
    instructionsFile: 'instructions.txt',
    instructionsError: 'לא ניתן לטעון את ההוראות כעת.',
    loadingInstructions: 'טוען הוראות...',
    helpLabel: 'עזרה',
    title: 'הרבי - משחק פרשנויות',
    logoAlt: 'הרב',
    roomCodeLabel: 'קוד חדר',
    helpTitle: 'איך משחקים?',
    closeHelp: 'סגור',
    helpTextAlign: 'right',
    roomJoin: {
      heading: 'הצטרפות למשחק',
      namePlaceholder: 'השם שלך',
      roomPlaceholder: 'קוד חדר',
      joinButton: 'הצטרפות לחדר',
      createButton: 'צור קוד חדר',
      playersTitle: 'שחקנים בחדר:',
    },
    common: {
      you: 'את/ה',
    },
    gameRoom: {
      exitButton: 'יציאה מהחדר',
      readyButton: 'אני מוכן/ה',
      waitingForOthers: 'ממתין/ה לשחקנים אחרים...',
      ask: {
        youAreAsker: 'את/ה השואל/ת! כתוב/כתבי את השאלה שלך',
        waitingFor: 'ממתינים לשאלה מאת {{name}}',
        questionFrom: 'השאלה של {{name}}:',
        questionGeneric: 'השאלה:',
      },
      interpret: {
        askerLabel: 'השואל/ת: {{name}}',
        rabbiAnswerLabel: 'תשובת הרב:',
      },
    },
    playerList: {
      title: 'שחקנים בחדר',
      playerHeader: 'שחקן',
      scoreHeader: 'ניקוד',
      readyHeader: 'מוכן/ה',
      readyYes: 'כן',
    },
    questionForm: {
      label: 'כתוב/כתבי את השאלה שלך',
      submitButton: 'שליחת שאלה',
    },
    interpretationForm: {
      timeRemaining: 'נותרו {{seconds}} שניות להגיש פרשנות',
      submitButton: 'שליחת פרשנות',
      submitted: 'הפרשנות נשלחה!',
      timeUp: 'נגמר הזמן — ממתינים לסיום הסבב',
    },
    roundResult: {
      title: 'תוצאות הסיבוב',
      winnerLabel: 'הזוכה:',
      tableHeaders: {
        player: 'שחקן',
        interpretation: 'פרשנות',
        score: 'ניקוד',
        reason: 'נימוק',
      },
      winnerReasonLabel: 'נימוק הזכייה:',
      readyButton: 'מוכן/ה לסיבוב הבא',
    },
  },
  en: {
    direction: 'ltr',
    toggleLabel: 'עברית',
    instructionsFile: 'instructions_en.txt',
    instructionsError: 'Unable to load the instructions right now.',
    loadingInstructions: 'Loading instructions...',
    helpLabel: 'Help',
    title: 'The Rabbi - A Game of Interpretations',
    logoAlt: 'The Rabbi',
    roomCodeLabel: 'Room Code',
    helpTitle: 'How to play?',
    closeHelp: 'Close',
    helpTextAlign: 'left',
    roomJoin: {
      heading: 'Join the Game',
      namePlaceholder: 'Your name',
      roomPlaceholder: 'Room code',
      joinButton: 'Join Room',
      createButton: 'Create Room Code',
      playersTitle: 'Players in the room:',
    },
    common: {
      you: 'You',
    },
    gameRoom: {
      exitButton: 'Leave Room',
      readyButton: "I'm Ready",
      waitingForOthers: 'Waiting for other players...',
      ask: {
        youAreAsker: "You're the asker! Write your question",
        waitingFor: 'Waiting for a question from {{name}}',
        questionFrom: "{{name}}'s question:",
        questionGeneric: 'Question:',
      },
      interpret: {
        askerLabel: 'Asker: {{name}}',
        rabbiAnswerLabel: "Rabbi's Answer:",
      },
    },
    playerList: {
      title: 'Players in the room',
      playerHeader: 'Player',
      scoreHeader: 'Score',
      readyHeader: 'Ready',
      readyYes: 'Yes',
    },
    questionForm: {
      label: 'Write your question',
      submitButton: 'Submit Question',
    },
    interpretationForm: {
      timeRemaining: 'You have {{seconds}} seconds left to submit an interpretation',
      submitButton: 'Submit Interpretation',
      submitted: 'Interpretation sent!',
      timeUp: 'Time is up — waiting for the round to finish',
    },
    roundResult: {
      title: 'Round Results',
      winnerLabel: 'Winner:',
      tableHeaders: {
        player: 'Player',
        interpretation: 'Interpretation',
        score: 'Score',
        reason: 'Reason',
      },
      winnerReasonLabel: 'Winning reason:',
      readyButton: 'Ready for the next round',
    },
  },
};

function App() {
  const [joined, setJoined] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [instructionsLines, setInstructionsLines] = useState([]);
  const [language, setLanguage] = useState('he');

  useEffect(() => {
    let id = localStorage.getItem('clientId');
    if (!id) {
      const cryptoObj = (typeof window !== 'undefined' && window.crypto) ? window.crypto : undefined;
      id = cryptoObj && typeof cryptoObj.randomUUID === 'function'
        ? cryptoObj.randomUUID()
        : Math.random().toString(36).slice(2);
      localStorage.setItem('clientId', id);
    }

    const savedName = localStorage.getItem('playerName');
    const savedRoom = localStorage.getItem('roomCode');
    if (savedName && savedRoom) {
      setPlayerName(savedName);
      setRoomCode(savedRoom);
      setJoined(true);
    }
  }, []);

  const activeTranslation = translations[language];

  useEffect(() => {
    let isMounted = true;
    const fetchInstructions = async () => {
      const instructionsPath = `${process.env.PUBLIC_URL || ''}/${activeTranslation.instructionsFile}`;
      if (isMounted) {
        setInstructionsLines([]);
      }
      try {
        const response = await fetch(instructionsPath);
        if (!response.ok) {
          throw new Error('Failed to load instructions');
        }
        const text = await response.text();
        const lines = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);
        if (isMounted) {
          setInstructionsLines(lines);
        }
      } catch (error) {
        if (isMounted) {
          setInstructionsLines([activeTranslation.instructionsError]);
        }
      }
    };

    fetchInstructions();

    return () => {
      isMounted = false;
    };
  }, [activeTranslation.instructionsError, activeTranslation.instructionsFile]);

  const handleCloseHelp = () => {
    setShowHelp(false);
  };

  const handleJoinComplete = (name, room) => {
    localStorage.setItem('playerName', name);
    localStorage.setItem('roomCode', room);
    setPlayerName(name);
    setRoomCode(room);
    setJoined(true);
  };

  const handleExit = () => {
    localStorage.removeItem('playerName');
    localStorage.removeItem('roomCode');
    setPlayerName('');
    setRoomCode('');
    setJoined(false);
  };

  const toggleLanguage = () => {
    setLanguage((prevLanguage) => (prevLanguage === 'he' ? 'en' : 'he'));
  };

  return (
    <div
      style={{
        direction: activeTranslation.direction,
        fontFamily: 'sans-serif',
        padding: '2em',
        maxWidth: '800px',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        position: 'relative',
      }}
    >
      <button
        type="button"
        className="language-toggle"
        onClick={toggleLanguage}
        style={
          activeTranslation.direction === 'rtl'
            ? { right: '1rem', left: 'auto' }
            : { left: '1rem', right: 'auto' }
        }
      >
        {activeTranslation.toggleLabel}
      </button>
      <button
        type="button"
        className="help-button"
        onClick={() => setShowHelp(true)}
        style={
          activeTranslation.direction === 'rtl'
            ? { left: '1rem', right: 'auto' }
            : { right: '1rem', left: 'auto' }
        }
      >
        {activeTranslation.helpLabel}
      </button>
      <h1
        style={{
          textAlign: 'center',
          color: '#4c6ef5',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <img
          src={rabbiLogo}
          alt={activeTranslation.logoAlt}
          style={{ height: '60px', borderRadius: '50%' }}
        />
        {activeTranslation.title}
      </h1>
      {joined && (
        <h2 style={{ textAlign: 'center', color: '#555' }}>
          {activeTranslation.roomCodeLabel}: {roomCode}
        </h2>
      )}

      {!joined ? (
        <RoomJoin
          onJoinComplete={handleJoinComplete}
          language={language}
          copy={activeTranslation.roomJoin}
        />
      ) : (
        <GameRoom
          playerName={playerName}
          roomCode={roomCode}
          onExit={handleExit}
          language={language}
          translations={activeTranslation}
        />
      )}
      {showHelp && (
        <div
          className="help-overlay"
          onClick={handleCloseHelp}
        >
          <div
            className="help-balloon"
            onClick={(event) => event.stopPropagation()}
            style={{
              direction: activeTranslation.direction,
              textAlign: activeTranslation.helpTextAlign,
            }}
          >
            <h3>{activeTranslation.helpTitle}</h3>
            <div className="help-text">
              {instructionsLines.length === 0 ? (
                <p>{activeTranslation.loadingInstructions}</p>
              ) : (
                instructionsLines.map((line, index) => (
                  <p key={index}>{line}</p>
                ))
              )}
            </div>
            <button
              type="button"
              className="close-help"
              onClick={(event) => {
                event.stopPropagation();
                handleCloseHelp();
              }}
            >
              {activeTranslation.closeHelp}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

